import React, {useCallback, useMemo, useRef, useState} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {NavigationContext} from '@react-navigation/native'

import {CHAT_DEFAULT_HOMESERVER, SOCIAL_DEFAULT_SERVICE} from '#/lib/constants'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {normalizeMatrixBaseUrl, setMatrixBaseUrl} from '#/lib/matrixClient'
import {loginMatrix} from '#/lib/matrixClient'
import {
  loadMatrixConfig,
  saveMatrixConfig,
  saveMatrixSession,
} from '#/lib/storage/matrix'
import {cleanError, isNetworkError} from '#/lib/strings/errors'
import {
  normalizeAtprotoIdentifier,
  normalizeMatrixIdentifier,
} from '#/lib/strings/identifiers'
import {
  useActivationActions,
  useActivationState,
  useIsActivationComplete,
} from '#/state/activation'
import {useSession, useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

type ActivationCardProps = {
  title: string
  description: string
  status: 'disconnected' | 'connecting' | 'connected' | 'error'
  error?: string
  onActivate: () => void
  onAdvanced: () => void
}

function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function normalizeServiceUrl(input: string, fallback: string) {
  const trimmed = input.trim()
  if (!trimmed) return fallback
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function formatServiceError(err: unknown) {
  if (isOffline()) {
    return 'Aucune connexion Internet.'
  }
  if (isNetworkError(err)) {
    return 'Service temporairement indisponible. Réessayez.'
  }
  const cleaned = cleanError(err)
  return cleaned || 'Impossible de se connecter.'
}

function StatusPill({status}: {status: ActivationCardProps['status']}) {
  const t = useTheme()
  const label =
    status === 'connected'
      ? 'Connecté'
      : status === 'connecting'
        ? 'En cours'
        : status === 'error'
          ? 'À réessayer'
          : 'À activer'
  const tone =
    status === 'connected'
      ? t.palette.positive_500
      : status === 'error'
        ? t.palette.negative_500
        : t.palette.contrast_500
  return (
    <View style={[styles.statusPill, {borderColor: tone}]}>
      <Text style={[styles.statusText, {color: tone}]}>{label}</Text>
    </View>
  )
}

function ActivationCard({
  title,
  description,
  status,
  error,
  onActivate,
  onAdvanced,
}: ActivationCardProps) {
  const t = useTheme()
  const buttonLabel =
    status === 'connected'
      ? 'Connecté'
      : status === 'connecting'
        ? 'Connexion...'
        : status === 'error'
          ? 'Réessayer'
          : 'Activer'
  const isLoading = status === 'connecting'
  return (
    <View style={[styles.card, t.atoms.bg]}>
      <View style={[a.gap_sm]}>
        <View style={[styles.cardHeader]}>
          <View style={[a.flex_1, a.gap_xs]}>
            <Text style={[styles.cardTitle, t.atoms.text]}>{title}</Text>
            <Text style={[styles.cardSubtitle, t.atoms.text_contrast_medium]}>
              {description}
            </Text>
          </View>
          <StatusPill status={status} />
        </View>

        {error ? (
          <View style={[styles.errorWrap]}>
            <Text style={[styles.errorText, {color: t.palette.negative_600}]}>
              {error}
            </Text>
          </View>
        ) : null}

        <Button
          label={buttonLabel}
          color="primary"
          size="large"
          variant="solid"
          disabled={status === 'connected' || isLoading}
          onPress={onActivate}
          style={styles.cardButton}>
          <ButtonText>{buttonLabel}</ButtonText>
          {status === 'connected' ? (
            <ButtonIcon icon={CheckIcon} />
          ) : isLoading ? (
            <ButtonIcon icon={Loader} />
          ) : null}
        </Button>

        <Button
          label="À propos / avancé"
          color="secondary"
          size="small"
          variant="ghost"
          onPress={onAdvanced}
          style={styles.advancedLink}>
          <ButtonText>
            <Trans>À propos / avancé</Trans>
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}

function ActivateSocialDialog({
  control,
  serviceUrl,
  setServiceUrl,
  advancedOpen,
  setAdvancedOpen,
}: {
  control: Dialog.DialogOuterProps['control']
  serviceUrl: string
  setServiceUrl: (value: string) => void
  advancedOpen: boolean
  setAdvancedOpen: (next: boolean) => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  const {login} = useSessionApi()
  const {setSocialStatus} = useActivationActions()
  const {isDesktop} = useWebMediaQueries()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const onConnect = useCallback(async () => {
    if (!identifier.trim() || !password) return
    setIsLoading(true)
    setError(null)
    setSocialStatus('connecting')
    try {
      const normalizedService = normalizeServiceUrl(
        serviceUrl,
        SOCIAL_DEFAULT_SERVICE,
      )
      const normalizedIdentifier = normalizeAtprotoIdentifier(
        identifier,
        normalizedService,
      )
      await login(
        {
          service: normalizedService,
          identifier: normalizedIdentifier,
          password,
          authFactorToken: '',
        },
        'ActivateSocial',
      )
      setSocialStatus('connected')
      setIsLoading(false)
      control.close()
    } catch (err) {
      const message = formatServiceError(err)
      setError(message)
      setSocialStatus('error', message)
      setIsLoading(false)
    }
  }, [identifier, password, serviceUrl, login, setSocialStatus, control])

  const dialogStyle = [
    styles.dialogInner,
    isDesktop && styles.dialogInnerDesktop,
  ]

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.Inner style={dialogStyle}>
        <View style={[a.gap_md]}>
          <Text style={[styles.dialogTitle, t.atoms.text]}>
            <Trans>Activer Tellus Social</Trans>
          </Text>
          <View style={[a.gap_sm]}>
            <View>
              <TextField.LabelText>
                <Trans>Pseudo ou e-mail</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={identifier}
                  onChangeText={setIdentifier}
                  label={_(msg`Pseudo ou e-mail`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </TextField.Root>
            </View>
            <View>
              <TextField.LabelText>
                <Trans>Mot de passe</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={password}
                  onChangeText={setPassword}
                  label={_(msg`Mot de passe`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry
                />
              </TextField.Root>
            </View>
          </View>

          {error ? (
            <Text style={[styles.errorText, {color: t.palette.negative_600}]}>
              {error}
            </Text>
          ) : null}

          <Button
            label={_(msg`Continuer`)}
            color="primary"
            size="large"
            variant="solid"
            disabled={isLoading || !identifier.trim() || !password}
            onPress={onConnect}
            style={styles.dialogButton}>
            <ButtonText>
              <Trans>Continuer</Trans>
            </ButtonText>
            {isLoading ? <ButtonIcon icon={Loader} /> : null}
          </Button>

          <Button
            label={_(msg`À propos / avancé`)}
            color="secondary"
            size="small"
            variant="ghost"
            onPress={() => setAdvancedOpen(!advancedOpen)}>
            <ButtonText>
              <Trans>À propos / avancé</Trans>
            </ButtonText>
          </Button>

          {advancedOpen ? (
            <View style={[a.gap_sm]}>
              <Text
                style={[styles.advancedTitle, t.atoms.text_contrast_medium]}>
                <Trans>Connexion (avancé)</Trans>
              </Text>
              <TextField.Root>
                <TextField.Input
                  value={serviceUrl}
                  onChangeText={setServiceUrl}
                  label={_(msg`https://bsky.social`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </TextField.Root>
              <Text style={[styles.advancedCopy, t.atoms.text_contrast_medium]}>
                <Trans>
                  Tellus s&apos;appuie sur des standards ouverts (AT Protocol).
                </Trans>
              </Text>
            </View>
          ) : null}
        </View>
      </Dialog.Inner>
    </Dialog.Outer>
  )
}

function ActivateChatDialog({
  control,
  homeserver,
  setHomeserver,
  advancedOpen,
  setAdvancedOpen,
}: {
  control: Dialog.DialogOuterProps['control']
  homeserver: string
  setHomeserver: (value: string) => void
  advancedOpen: boolean
  setAdvancedOpen: (next: boolean) => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  const {setChatStatus} = useActivationActions()
  const {isDesktop} = useWebMediaQueries()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [emailHelp, setEmailHelp] = useState(false)
  const requestIdRef = useRef(0)

  const onConnect = useCallback(async () => {
    if (!identifier.trim() || !password) return
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setIsLoading(true)
    setError(null)
    setChatStatus('connecting')
    const normalizedHomeserver = normalizeMatrixBaseUrl(homeserver)
    if (!normalizedHomeserver) {
      setError('Service temporairement indisponible. Réessayez.')
      setChatStatus('error', 'Service temporairement indisponible. Réessayez.')
      setAdvancedOpen(true)
      setIsLoading(false)
      return
    }
    try {
      setMatrixBaseUrl(normalizedHomeserver)
      const normalizedIdentifier = normalizeMatrixIdentifier(
        identifier,
        normalizedHomeserver,
      )
      const response = await loginMatrix(normalizedIdentifier, password)
      if (requestIdRef.current !== requestId) return
      const nextSession = {
        accessToken: response.access_token,
        userId: response.user_id,
        deviceId: response.device_id,
      }
      await saveMatrixConfig({homeserver: normalizedHomeserver})
      await saveMatrixSession(nextSession)
      setEmailHelp(false)
      setChatStatus('connected')
      setIsLoading(false)
      control.close()
    } catch (err) {
      if (requestIdRef.current !== requestId) return
      const message = formatServiceError(err)
      const looksLikeEmail =
        identifier.includes('@') && !identifier.trim().startsWith('@')
      if (looksLikeEmail) {
        setEmailHelp(true)
      }
      setError(message)
      setChatStatus('error', message)
      setIsLoading(false)
    }
  }, [
    identifier,
    password,
    homeserver,
    setChatStatus,
    setAdvancedOpen,
    control,
  ])

  React.useEffect(() => {
    if (!isLoading) return
    const requestId = requestIdRef.current
    const timeoutId = setTimeout(() => {
      if (requestIdRef.current !== requestId) return
      const message = 'Service temporairement indisponible. Réessayez.'
      setError(message)
      setChatStatus('error', message)
      setIsLoading(false)
    }, 15000)
    return () => clearTimeout(timeoutId)
  }, [isLoading, setChatStatus])

  const dialogStyle = [
    styles.dialogInner,
    isDesktop && styles.dialogInnerDesktop,
  ]

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.Inner style={dialogStyle}>
        <View style={[a.gap_md]}>
          <Text style={[styles.dialogTitle, t.atoms.text]}>
            <Trans>Activer Tellus Chat</Trans>
          </Text>
          <View style={[a.gap_sm]}>
            <View>
              <TextField.LabelText>
                <Trans>Pseudo ou e-mail</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={identifier}
                  onChangeText={setIdentifier}
                  label={_(msg`Pseudo ou e-mail`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </TextField.Root>
              {emailHelp ? (
                <Text style={[styles.emailHelp, t.atoms.text_contrast_medium]}>
                  <Trans>Utilisez votre identifiant @nom:domaine.</Trans>
                </Text>
              ) : null}
            </View>
            <View>
              <TextField.LabelText>
                <Trans>Mot de passe</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={password}
                  onChangeText={setPassword}
                  label={_(msg`Mot de passe`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry
                />
              </TextField.Root>
            </View>
          </View>

          {error ? (
            <Text style={[styles.errorText, {color: t.palette.negative_600}]}>
              {error}
            </Text>
          ) : null}

          <Button
            label={_(msg`Continuer`)}
            color="primary"
            size="large"
            variant="solid"
            disabled={isLoading || !identifier.trim() || !password}
            onPress={onConnect}
            style={styles.dialogButton}>
            <ButtonText>
              <Trans>Continuer</Trans>
            </ButtonText>
            {isLoading ? <ButtonIcon icon={Loader} /> : null}
          </Button>

          <Button
            label={_(msg`À propos / avancé`)}
            color="secondary"
            size="small"
            variant="ghost"
            onPress={() => setAdvancedOpen(!advancedOpen)}>
            <ButtonText>
              <Trans>À propos / avancé</Trans>
            </ButtonText>
          </Button>

          {advancedOpen ? (
            <View style={[a.gap_sm]}>
              <Text
                style={[styles.advancedTitle, t.atoms.text_contrast_medium]}>
                <Trans>Serveur (avancé)</Trans>
              </Text>
              <TextField.Root>
                <TextField.Input
                  value={homeserver}
                  onChangeText={setHomeserver}
                  label={CHAT_DEFAULT_HOMESERVER}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </TextField.Root>
              <Text style={[styles.advancedCopy, t.atoms.text_contrast_medium]}>
                <Trans>Tellus Chat s&apos;appuie sur Matrix.</Trans>
              </Text>
            </View>
          ) : null}
        </View>
      </Dialog.Inner>
    </Dialog.Outer>
  )
}

export function ActivateTellusScreen() {
  const {_} = useLingui()
  const t = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = React.useContext(NavigationContext)
  const {isDesktop} = useWebMediaQueries()
  const {hasSession} = useSession()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const {setChatStatus} = useActivationActions()
  const {social, chat} = useActivationState()
  const isComplete = useIsActivationComplete()
  const socialDialog = Dialog.useDialogControl()
  const chatDialog = Dialog.useDialogControl()
  const [showDetails, setShowDetails] = useState(false)
  const [socialAdvancedOpen, setSocialAdvancedOpen] = useState(false)
  const [chatAdvancedOpen, setChatAdvancedOpen] = useState(false)
  const [serviceUrl, setServiceUrl] = useState(SOCIAL_DEFAULT_SERVICE)
  const [homeserver, setHomeserver] = useState(CHAT_DEFAULT_HOMESERVER)

  React.useEffect(() => {
    let active = true
    loadMatrixConfig()
      .then(config => {
        if (!active) return
        if (config?.homeserver) {
          setHomeserver(config.homeserver)
        }
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  React.useEffect(() => {
    if (chat.status !== 'connecting') return
    const timeoutId = setTimeout(() => {
      const message = 'Service temporairement indisponible. Réessayez.'
      setChatStatus('error', message)
    }, 15000)
    return () => clearTimeout(timeoutId)
  }, [chat.status, setChatStatus])

  const openSocial = useCallback(
    (advanced: boolean) => {
      setSocialAdvancedOpen(advanced)
      socialDialog.open()
    },
    [socialDialog],
  )

  const openChat = useCallback(
    (advanced: boolean) => {
      setChatAdvancedOpen(advanced)
      chatDialog.open()
    },
    [chatDialog],
  )

  const onCreateAccount = useCallback(() => {
    requestSwitchToAccount({requestedAccount: 'new'})
  }, [requestSwitchToAccount])

  const onContinue = useCallback(() => {
    if (!isComplete) return
    navigation?.navigate?.('Home' as never)
  }, [isComplete, navigation])

  const containerStyle = useMemo(
    () => [
      styles.container,
      t.atoms.bg,
      {paddingTop: insets.top + 24, paddingBottom: insets.bottom + 24},
      isDesktop && styles.containerDesktop,
    ],
    [t, insets, isDesktop],
  )

  return (
    <ScrollView contentContainerStyle={containerStyle}>
      <View style={styles.inner}>
        <View style={[a.gap_sm]}>
          <Text style={[styles.title, t.atoms.text]}>
            <Trans>Activer Tellus</Trans>
          </Text>
          <Text style={[styles.subtitle, t.atoms.text_contrast_medium]}>
            <Trans>Active Social et Chat pour continuer.</Trans>
          </Text>
        </View>

        {!hasSession ? (
          <View style={[styles.card, t.atoms.bg]}>
            <View style={[a.gap_sm]}>
              <Text style={[styles.signupTitle, t.atoms.text]}>
                <Trans>Créer votre compte Tellus</Trans>
              </Text>
              <Text
                style={[styles.signupSubtitle, t.atoms.text_contrast_medium]}>
                <Trans>Une seule étape, puis activation.</Trans>
              </Text>
              <Button
                label={_(msg`Créer votre compte`)}
                color="secondary"
                size="large"
                variant="outline"
                onPress={onCreateAccount}
                style={styles.signupButton}>
                <ButtonText>
                  <Trans>Créer votre compte</Trans>
                </ButtonText>
              </Button>
            </View>
          </View>
        ) : null}

        <ActivationCard
          title="Tellus Social"
          description={_(msg`Publier, suivre, découvrir.`)}
          status={social.status}
          error={social.error}
          onActivate={() => openSocial(false)}
          onAdvanced={() => openSocial(true)}
        />

        <ActivationCard
          title="Tellus Chat"
          description={_(msg`Messages privés et groupes.`)}
          status={chat.status}
          error={chat.error}
          onActivate={() => openChat(false)}
          onAdvanced={() => openChat(true)}
        />

        <Button
          label={_(msg`Continuer`)}
          color="primary"
          size="large"
          variant="solid"
          disabled={!isComplete}
          onPress={onContinue}
          style={styles.continueButton}>
          <ButtonText>
            <Trans>Continuer</Trans>
          </ButtonText>
        </Button>

        <Button
          label={_(msg`Détails`)}
          color="secondary"
          size="small"
          variant="ghost"
          onPress={() => setShowDetails(!showDetails)}>
          <ButtonText>
            <Trans>Détails</Trans>
          </ButtonText>
        </Button>

        {showDetails ? (
          <Text style={[styles.legal, t.atoms.text_contrast_medium]}>
            <Trans>
              Tellus s&apos;appuie sur des standards ouverts (AT Protocol,
              Matrix). Certains services peuvent être hébergés par des
              fournisseurs compatibles.
            </Trans>
          </Text>
        ) : null}
      </View>

      <ActivateSocialDialog
        control={socialDialog}
        serviceUrl={serviceUrl}
        setServiceUrl={setServiceUrl}
        advancedOpen={socialAdvancedOpen}
        setAdvancedOpen={setSocialAdvancedOpen}
      />
      <ActivateChatDialog
        control={chatDialog}
        homeserver={homeserver}
        setHomeserver={setHomeserver}
        advancedOpen={chatAdvancedOpen}
        setAdvancedOpen={setChatAdvancedOpen}
      />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
  },
  containerDesktop: {
    paddingHorizontal: 32,
  },
  inner: {
    maxWidth: 420,
    width: '100%',
    alignSelf: 'center',
    gap: 16,
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 22,
    opacity: 0.7,
  },
  card: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
    padding: 18,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 2},
    elevation: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  cardSubtitle: {
    fontSize: 14,
    opacity: 0.7,
  },
  cardButton: {
    height: 50,
    borderRadius: 14,
    width: '100%',
  },
  signupTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  signupSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.7,
  },
  signupButton: {
    height: 50,
    borderRadius: 14,
    width: '100%',
  },
  advancedLink: {
    alignSelf: 'flex-start',
  },
  statusPill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  errorWrap: {
    borderRadius: 12,
    backgroundColor: 'rgba(255, 0, 0, 0.05)',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  errorText: {
    fontSize: 13,
  },
  continueButton: {
    height: 52,
    borderRadius: 16,
    width: '100%',
  },
  legal: {
    fontSize: 12,
    lineHeight: 18,
  },
  dialogInner: {
    paddingTop: 24,
  },
  dialogInnerDesktop: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 420,
  },
  dialogTitle: {
    fontSize: 22,
    fontWeight: '700',
  },
  dialogButton: {
    height: 50,
    borderRadius: 14,
  },
  advancedTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  advancedCopy: {
    fontSize: 12,
    lineHeight: 18,
  },
  emailHelp: {
    fontSize: 12,
    marginTop: 6,
  },
})
